import { useMemo, useState } from "react";
import { ArrowDownAZ, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Toggle } from "@/components/ui/toggle";
import { useNewsSources } from "@/hooks/api";
import { useToggleState } from "@/hooks/useToggleState";
import { cn } from "@/lib/utils";

type NameOrder = null | "asc" | "desc";

export function NewsSidebar({
  selected,
  onSelect,
}: {
  selected: number | null;
  onSelect: (id: number) => void;
}) {
  const [query, setQuery] = useState("");
  const [nameOrder, cycleName] = useToggleState<NameOrder>([null, "asc", "desc"]);
  const { data, isLoading } = useNewsSources(query || undefined);

  const list = useMemo(() => {
    const arr = [...(data ?? [])];
    if (nameOrder) {
      arr.sort((a, b) =>
        nameOrder === "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name),
      );
    }
    return arr;
  }, [data, nameOrder]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b border-border space-y-2">
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search providers..."
              className="pl-7 h-7 text-[12px]"
            />
          </div>
          <Toggle
            pressed={!!nameOrder}
            onPressedChange={cycleName}
            size="sm"
            className="h-6 px-1.5 rounded-[4px] border border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary"
            title="Sort by name"
          >
            <ArrowDownAZ className="h-3 w-3" />
            {nameOrder && (
              <span className="text-[9px] font-mono opacity-80 ml-0.5">
                {nameOrder === "asc" ? "A↓Z" : "Z↓A"}
              </span>
            )}
          </Toggle>
        </div>
        <div className="text-[10px] text-muted-foreground font-mono">
          {isLoading ? "…" : `${list.length} providers`}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">Loading…</div>
        )}
        {!isLoading && list.length === 0 && (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
            No content sources registered yet.
          </div>
        )}
        {list.map((p) => (
          <button
            key={p.id}
            onClick={() => onSelect(p.id)}
            className={cn(
              "w-full text-left px-2 py-1.5 border-b border-border/60 hover:bg-accent/60 transition-colors block min-h-[44px] md:min-h-0",
              selected === p.id && "bg-accent",
            )}
          >
            <div className="flex items-baseline justify-between gap-2">
              <div className="font-semibold text-[12px] truncate">{p.name}</div>
              <div className="font-mono text-[10px] text-muted-foreground tabular-nums">
                {p.scraped_count.toLocaleString()}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[9px] font-mono text-muted-foreground">
              <span>imp {p.importance_score ?? "—"}</span>
              <span>·</span>
              <span>rel {p.relevance_score ?? "—"}</span>
              <span>·</span>
              <span>pred {p.prediction_importance_score ?? "—"}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
